import { useState } from "react";
import { CheckCircle2, Circle, Plus, Trash2, Flame, Star } from "lucide-react";
import { useWellnessState } from "@/context/WellnessStateContext";
import { usePeriod } from "@/context/PeriodContext";
import { glowTasks, glowCadences, luxuryBlueprint, cadenceHorizon, glowStreak, uid } from "@/lib/wellness";
import { periodKey } from "@/lib/life";
import { PageHeader, SectionCard, ProgressBar, AddButton, EmptyState } from "@/components/shared";
import { PeriodNav } from "@/components/PeriodNav";
import { FormModal, TextField, SelectField } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const cadenceOpts = glowCadences.map((c) => ({ value: c, label: c }));

export default function Glow() {
  const { state, update, loading } = useWellnessState();
  const { horizon, setHorizon, date, setDate } = usePeriod();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const log = state.glowLog || {};
  const customTasks = (state.customGlowTasks || []).map((t) => ({ ...t, icon: Star, custom: true }));
  const allTasks = [...glowTasks, ...customTasks];
  const keyFor = (task) => periodKey(cadenceHorizon(task.cadence), date);
  const isDone = (task) => !!(log[keyFor(task)] || {})[task.id];
  const toggle = (task) => update((prev) => {
    const gl = { ...(prev.glowLog || {}) };
    const k = keyFor(task);
    gl[k] = { ...(gl[k] || {}), [task.id]: !(gl[k] || {})[task.id] };
    return { ...prev, glowLog: gl };
  }, "Updated glow schedule");

  const done = allTasks.filter(isDone).length;

  const openNew = () => { setDraft({ id: uid("glow"), label: "", cadence: "Daily" }); setOpen(true); };
  const saveRitual = () => {
    if (!draft.label.trim()) return toast.error("Give your ritual a name");
    update((prev) => ({ ...prev, customGlowTasks: [...(prev.customGlowTasks || []), { ...draft, label: draft.label.trim() }] }), "Added custom ritual");
    setOpen(false); toast.success("Ritual added to your schedule");
  };
  const removeRitual = (id) => {
    update((prev) => {
      const gl = { ...(prev.glowLog || {}) };
      Object.keys(gl).forEach((k) => { if (gl[k] && id in gl[k]) { const c = { ...gl[k] }; delete c[id]; gl[k] = c; } });
      return { ...prev, customGlowTasks: (prev.customGlowTasks || []).filter((t) => t.id !== id), glowLog: gl };
    }, "Removed custom ritual");
    toast.success("Ritual removed");
  };

  return (
    <div>
      <PageHeader title="Glow & Presence" subtitle="A schedule for radiant presence — check off each ritual as you complete it." testid="wellness-glow-header"
        actions={<AddButton onClick={openNew} testid="add-ritual-button"><Plus className="h-4 w-4 mr-1.5" />Add ritual</AddButton>} />

      <div className="mb-4"><PeriodNav horizon={horizon} setHorizon={setHorizon} date={date} setDate={setDate} testid="glow-period" /></div>

      <SectionCard className="mb-4" testid="glow-progress-card">
        <div className="flex items-center justify-between gap-4">
          <div><h3 className="font-serif-display text-xl text-[var(--text)]">Glow schedule</h3><p className="text-sm text-[var(--text-muted)] mt-0.5">Each ritual is tracked within its own cadence, anchored to the date you're viewing. Keep a streak going for a glowing badge.</p></div>
          <div className="min-w-[150px]"><div className="flex items-center justify-between text-xs text-[var(--text-muted)] mb-1"><span>Completed</span><span className="tabular-nums">{done}/{allTasks.length}</span></div><ProgressBar value={done} max={allTasks.length} tone="gold" /></div>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {glowCadences.map((cadence) => {
          const items = allTasks.filter((t) => t.cadence === cadence);
          return (
            <SectionCard key={cadence} title={cadence} subtitle={`Tracked per ${cadenceHorizon(cadence).toLowerCase()}`} testid={`glow-group-${cadence}`}>
              <div className="space-y-2">
                {items.length === 0 && <EmptyState icon={Star} title="No rituals yet" message="Add a ritual to schedule it here." />}
                {items.map((t) => {
                  const on = isDone(t); const Icon = t.icon; const streak = glowStreak(log, t.id, t.cadence, date);
                  return (
                    <div key={t.id} className={cn("flex items-center gap-2 rounded-[10px] border px-2 py-1.5 transition-colors", on ? "border-[color:rgba(198,139,60,0.5)] bg-[color:rgba(198,139,60,0.08)]" : "border-[color:var(--border-subtle)] bg-white")} data-testid={`glow-row-${t.id}`}>
                      <button onClick={() => toggle(t)} data-testid={`glow-task-${t.id}`} className="flex items-center gap-2.5 flex-1 min-w-0 text-left rounded-[8px] px-1 py-1 hover:bg-[color:rgba(23,35,31,0.03)] transition-colors">
                        {on ? <CheckCircle2 className="h-5 w-5 text-[var(--gold)] shrink-0" /> : <Circle className="h-5 w-5 text-[var(--text-faint)] shrink-0" />}
                        <Icon className="h-4 w-4 text-[var(--text-muted)] shrink-0" />
                        <span className="text-sm text-[var(--text)] truncate">{t.label}</span>
                      </button>
                      {streak >= 2 && (
                        <span data-testid={`glow-streak-${t.id}`} title={`${streak} in a row`}
                          className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-[var(--gold)] rounded-full px-1.5 py-0.5 bg-[color:rgba(198,139,60,0.14)] shrink-0"
                          style={{ boxShadow: "0 0 8px rgba(198,139,60,0.45)" }}>
                          <Flame className="h-3 w-3" />{streak}
                        </span>
                      )}
                      {t.custom && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--danger)] shrink-0" onClick={() => removeRitual(t.id)} data-testid={`glow-delete-${t.id}`} aria-label="Delete ritual"><Trash2 className="h-3.5 w-3.5" /></Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          );
        })}
      </div>

      <SectionCard title="Luxury blueprint" subtitle="The pillars behind the schedule" testid="luxury-blueprint-card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {luxuryBlueprint.map((b) => { const Icon = b.icon; return (
            <div key={b.title} className="rounded-[14px] border border-[color:var(--border-subtle)] bg-white p-4" data-testid={`blueprint-${b.title}`}>
              <div className="flex items-center gap-2"><span className="grid place-items-center h-9 w-9 rounded-[10px] bg-[color:rgba(31,115,85,0.1)] text-[var(--green)]"><Icon className="h-4 w-4" /></span><span className="text-[10px] uppercase tracking-wide text-[var(--text-faint)]">{b.cadence}</span></div>
              <h4 className="font-serif-display text-lg text-[var(--text)] mt-3">{b.title}</h4>
              <p className="text-[13px] text-[var(--text-muted)] mt-1 leading-relaxed">{b.detail}</p>
            </div>
          ); })}
        </div>
      </SectionCard>

      {draft && (
        <FormModal open={open} onOpenChange={setOpen} title="Add a glow ritual" onSubmit={saveRitual} testid="ritual-modal"
          description="Create your own ritual and choose how often it repeats.">
          <TextField label="Ritual" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} full testid="ritual-label-input" placeholder="e.g. Lymphatic body brushing" />
          <SelectField label="Repeats" value={draft.cadence} onChange={(v) => setDraft({ ...draft, cadence: v })} options={cadenceOpts} testid="ritual-cadence-select" />
        </FormModal>
      )}
    </div>
  );
}
